<template>
  <div>
    <FileInput
      :accept="props.acceptedFileTypes?.join(',') || undefined"
      :label="t('Reviews.product.edit.documents.file.label')"
      :helper-text="fileHint"
      :file-name="formData.filename"
      :error="getValidationErrors(v$.file)"
      @input="onFilePicked"
    />
    <FileInput
      :accept="props.acceptedThumbnailTypes?.join(',') || undefined"
      deletable
      :label="t('Reviews.product.edit.documents.thumbnail.label')"
      :helper-text="thumbnailHint"
      :file-name="formData.previewFile?.name || formData.previewFilename"
      :error="getValidationErrors(v$.previewFile)"
      data-testid="thumbnail-upload-input"
      @input="onThumbnailPicked"
    />
    <div v-for="lang in ALLOWED_LOCALES" :key="lang" class="mt-2">
      <VuelidateField :validation="v$.name[lang]">
        <template #default="{ error, state, countHint }">
          <FieldWrapper :for="`document-title-${lang}`" :error="error" :count-hint="countHint">
            <template #label>
              <div class="flex items-center justify-between">
                <span>{{ t('Reviews.product.edit.documents.name.label') }}</span>
                <ChipBadge class="uppercase" size="s" color="purple" rounded>{{ lang }}</ChipBadge>
              </div>
            </template>
            <template #default="{ id }">
              <div class="mt-2">
                <InputField
                  :id="id"
                  v-model="v$.name[lang].$model"
                  :data-testid="`document-title-${lang}`"
                  type="text"
                  :state="state"
                  :maxlength="DOCUMENT_NAME_MAX_LENGTH"
                  @change="onNameChange"
                />
              </div>
            </template>
          </FieldWrapper>
        </template>
      </VuelidateField>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ChipBadge, FieldWrapper, InputField } from '@ramp106/omrjs-core-ui'
import { FileInput } from '@ramp106/omrjs-core-ui/deprecated'
import { useVuelidate, type ValidationArgs } from '@vuelidate/core'
import { computed, watch, reactive, onMounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'

import type { ReviewsEntityDocumentEditData } from '@/components/ReviewsManagement/types.ts'
import { ALLOWED_LOCALES } from '@/helpers/translationsHelper.ts'
import { buildTranslationFieldsRules, fileMaxSizeRule, fileMimeTypeRule, getValidationErrors } from '@/helpers/validationHelper.ts'
import { maxLength, minLength } from '@/services/validators.ts'
import VuelidateField from '@/ui/VuelidateField.vue'

import {
  DOCUMENT_MAX_FILE_MB_SIZE,
  DOCUMENT_MAX_THUMBNAIL_FILE_MB_SIZE,
  DOCUMENT_NAME_MAX_LENGTH,
  DOCUMENT_NAME_MIN_LENGTH,
} from './constants.ts'

const props = defineProps<{
  value: ReviewsEntityDocumentEditData
  index: number
  acceptedFileTypes: string[]
  acceptedThumbnailTypes?: string[]
}>()
const emit = defineEmits<{
  (e: 'input', value: ReviewsEntityDocumentEditData): void
}>()

const { t } = useI18n()

const fileHint = computed(() => {
  const acceptedMimeTypes = props.acceptedFileTypes?.map((type) => `${type.split('/')[1]}`).join(', ')
  return `${acceptedMimeTypes} ・ Max. ${DOCUMENT_MAX_FILE_MB_SIZE} MB`
})

const thumbnailHint = computed(() => {
  const acceptedMimeTypes = props.acceptedThumbnailTypes?.map((type) => `${type.split('/')[1]}`).join(', ')
  return `${acceptedMimeTypes} ・ Max. ${DOCUMENT_MAX_THUMBNAIL_FILE_MB_SIZE} MB`
})

const rules = computed<ValidationArgs<Pick<ReviewsEntityDocumentEditData, 'name' | 'file' | 'previewFile'>>>(() => ({
  name: buildTranslationFieldsRules({
    minLength: minLength(DOCUMENT_NAME_MIN_LENGTH),
    maxLength: maxLength(DOCUMENT_NAME_MAX_LENGTH),
  }),
  file: {
    fileSize: fileMaxSizeRule(t, { megaBytes: DOCUMENT_MAX_FILE_MB_SIZE }),
    fileType: fileMimeTypeRule(t, { mimeTypes: props.acceptedFileTypes || [] }),
  },
  previewFile: {
    fileSize: fileMaxSizeRule(t, { megaBytes: DOCUMENT_MAX_FILE_MB_SIZE }),
    fileType: fileMimeTypeRule(t, { mimeTypes: props.acceptedThumbnailTypes || [] }),
  },
}))

const formData = reactive<ReviewsEntityDocumentEditData>({
  ...props.value,
})

const v$ = useVuelidate(rules, formData, { $registerAs: `document-${props.index}` })

function onNameChange() {
  formData.updated = true
}

function onFilePicked(file: File | null) {
  if (!file) {
    return
  }
  formData.file = file
  formData.size = file.size
  formData.contentType = file.type
  formData.filename = file.name
  formData.updated = true
  v$.value.file.$touch()
}

function onThumbnailPicked(file: File | null) {
  if (file) {
    formData.previewFile = file
    formData.updated = true
    formData.previewDeleted = false
  } else {
    formData.previewFile = null
    formData.previewDeleted = true
    formData.updated = true
  }
  v$.value.previewFile.$touch()
}

onMounted(() => {
  nextTick(() => {
    v$.value.file.$touch()
    ALLOWED_LOCALES.forEach((lang) => v$.value.name[lang].$touch())
  })
})

watch(
  () => formData,
  () => {
    emit('input', formData)
  },
  { deep: true },
)
</script>
